import { readonly, ref, watch } from 'vue'

import type { StockStepsOptions } from '@/domains/command-center/composables/use-stock-mode/useStockSteps.types'
import type { StockModeStep, StockSideTrip } from '@/domains/command-center/handlers/stock/stockMode.types'

import { MAIN_STEPS, SIDE_TRIP_STEPS } from '@/domains/command-center/handlers/stock/stockMode.consts'

/**
 * Where stock mode is, and what the search box holds in each step.
 *
 * The box holds the search in the item step and the number in the quantity step, so each step's text is kept
 * here and shown again on return. A side trip — the reason or location list — returns to the main step it
 * was opened from.
 *
 * ⚠ **Only this composable writes the step texts.** `goTo` writes the text and the box together, so the
 * watcher below sees its own write as no change. Callers read the texts and move with `goTo`.
 */
const useStockSteps = ({ query, isActive, onQuantityEdited }: StockStepsOptions) => {
  const step = ref<StockModeStep>('item')
  const returnStep = ref<StockModeStep>('item')
  const searchText = ref('')
  const quantityText = ref('')

  const textFor = (target: StockModeStep): string => {
    if (target === 'item') return searchText.value
    if (target === 'quantity') return quantityText.value

    return ''
  }

  const goTo = (target: StockModeStep, text = textFor(target)): void => {
    if (target === 'item') searchText.value = text
    if (target === 'quantity') quantityText.value = text

    step.value = target
    query.value = text
  }

  /**
   * ⚠ **The return step is recorded only when leaving a main step.** A side trip opened from another side
   * trip — the location chip pressed on the reason list — would otherwise become its own way back, and loop.
   */
  const openSideTrip = (target: StockSideTrip): void => {
    if (MAIN_STEPS.includes(step.value)) returnStep.value = step.value

    goTo(target, '')
  }

  const closeSideTrip = (): void => goTo(returnStep.value)

  const reset = (): void => {
    step.value = 'item'
    returnStep.value = 'item'
    searchText.value = ''
    quantityText.value = ''
  }

  // While stock mode is active, the box speaks for the current step.
  watch(query, text => {
    if (!isActive.value) return

    const value = text ?? ''

    if (step.value === 'item') searchText.value = value

    if (step.value === 'quantity' && value !== quantityText.value) {
      quantityText.value = value
      onQuantityEdited()
    }
  })

  return {
    step: readonly(step),
    searchText: readonly(searchText),
    quantityText: readonly(quantityText),
    goTo,
    openSideTrip,
    closeSideTrip,
    reset
  }
}

/** Whether a step is a side trip, which returns to the step it was opened from. */
const isSideTrip = (step: StockModeStep): step is StockSideTrip => (SIDE_TRIP_STEPS as readonly string[]).includes(step)

export { isSideTrip, useStockSteps }
